import React, { useMemo, useState } from 'react';
import { useSEO } from '../hooks/useSEO';

const SUPPORT_EMAIL = 'doc@eidoc.com.br';

function capture(name, properties = {}) {
  if (typeof window !== 'undefined' && window.eidocAnalytics?.capture) {
    window.eidocAnalytics.capture(name, { product: 'minimental', surface: 'support', ...properties });
  }
}

function classify(result) {
  if (result.error) return { tone: 'warning', label: 'Sem resposta', detail: 'A conexão não respondeu. Tente novamente em alguns segundos.' };
  if (result.ok) return { tone: 'success', label: 'Funcionando', detail: 'A rota respondeu normalmente.' };
  if (result.edgeLikely) return { tone: 'warning', label: 'Possível bloqueio na borda', detail: `HTTP ${result.status}. A resposta parece ter vindo de uma proteção de borda.` };
  return { tone: 'danger', label: 'Falha da aplicação', detail: `HTTP ${result.status}. A resposta parece ter vindo da aplicação.` };
}

async function inspectEndpoint(label, path) {
  const startedAt = Date.now();
  try {
    const response = await fetch(path, { credentials: 'same-origin', cache: 'no-store' });
    const contentType = response.headers.get('content-type') || '';
    const cfRay = response.headers.get('cf-ray') || '';
    const cfMitigated = response.headers.get('cf-mitigated') || '';
    return {
      label,
      path,
      ok: response.ok,
      status: response.status,
      duration: Date.now() - startedAt,
      contentType: contentType.split(';')[0],
      edgeLikely: Boolean(cfMitigated) || (response.status >= 403 && contentType.includes('text/html')),
      edgeSignal: Boolean(cfMitigated || cfRay),
    };
  } catch {
    return { label, path, error: true, duration: Date.now() - startedAt };
  }
}

function makeReport(results) {
  const now = new Date().toISOString();
  const browser = typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 120) : 'indisponível';
  const lines = [
    'MiniMental — diagnóstico técnico',
    `Horário UTC: ${now}`,
    `Página: ${typeof window !== 'undefined' ? window.location.pathname : '/suporte'}`,
    `Navegador: ${browser}`,
    '',
    ...results.map((result) => {
      const status = result.error ? 'sem resposta' : `HTTP ${result.status}`;
      const signal = result.edgeSignal ? 'sinal Cloudflare presente' : 'sem sinal Cloudflare específico';
      return `- ${result.label} (${result.path}): ${status}; ${signal}; ${result.duration} ms`;
    }),
    '',
    'Nenhum dado de paciente, senha, token ou conteúdo de formulário faz parte deste diagnóstico.',
  ];
  return lines.join('\\n');
}

export default function Support() {
  useSEO({
    title: 'Suporte e diagnóstico — MiniMental.com.br',
    description: 'Verifique se uma dificuldade de acesso ao MiniMental vem da aplicação ou de uma proteção de rede e envie um diagnóstico técnico sanitizado.',
    path: '/suporte',
  });

  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const report = useMemo(() => makeReport(results), [results]);

  async function runDiagnostic() {
    capture('support_diagnostic_started');
    setBusy(true);
    setCopied(false);
    const next = await Promise.all([
      inspectEndpoint('Sessão', '/api/auth/session'),
      inspectEndpoint('Acesso PRO', '/api/pro/access'),
      inspectEndpoint('Página pública', '/favicon.svg'),
    ]);
    setResults(next);
    setBusy(false);
    capture('support_diagnostic_completed', {
      checks: next.length,
      failed_checks: next.filter((item) => !item.ok).length,
      edge_signals: next.filter((item) => item.edgeSignal).length,
    });
  }

  async function copyReport() {
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      capture('support_report_copied');
    } catch {
      setCopied(false);
    }
  }

  function contactSupport() {
    capture('support_contact_clicked');
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=Suporte%20MiniMental`;
  }

  return (
    <main>
      <section className="mx-auto max-w-[980px] px-4 py-14 md:py-20">
        <div className="max-w-3xl">
          <p className="text-xs font-semibold text-[var(--text-muted)]">Suporte</p>
          <h1 className="mt-4 leading-tight text-[var(--primary)]">Vamos descobrir onde está o problema.</h1>
          <p className="mt-5 text-lg leading-relaxed text-[var(--text-muted)]">
            Esta verificação testa rotas públicas do MiniMental e informa se a resposta parece normal, falhou na aplicação ou foi interrompida por uma proteção de borda.
          </p>
        </div>

        <section className="mt-10 rounded-3xl border border-[var(--border)] bg-[var(--surface)] p-6 shadow-sm md:p-8">
          <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-start">
            <div>
              <h2 className="text-2xl font-semibold text-[var(--primary)]">Diagnóstico rápido</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[var(--text-muted)]">
                Não envia dados clínicos. O diagnóstico registra somente status HTTP, tempo de resposta e sinais técnicos da rede.
              </p>
            </div>
            <button type="button" onClick={runDiagnostic} disabled={busy} className="shrink-0 rounded-xl bg-[var(--accent)] px-5 py-3 font-bold text-[var(--on-accent)] transition hover:brightness-95 disabled:opacity-60">
              {busy ? 'Verificando…' : results.length ? 'Verificar novamente' : 'Verificar agora'}
            </button>
          </div>

          {results.length ? (
            <div className="mt-7 space-y-3">
              {results.map((result) => {
                const state = classify(result);
                const colors = state.tone === 'success'
                  ? 'border-[var(--pen)] bg-[var(--success-bg)] text-[var(--success-text)]'
                  : state.tone === 'warning'
                    ? 'border-[var(--pen)] bg-[var(--warning-bg)] text-[var(--warning-text)]'
                    : 'border-[var(--stamp)] bg-[var(--danger-bg)] text-[var(--danger-text)]';
                return (
                  <div key={result.path} className={`rounded-2xl border p-4 ${colors}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-bold">{result.label}</p>
                      <span className="text-sm font-bold">{state.label}</span>
                    </div>
                    <p className="mt-1 text-sm">{state.detail}</p>
                    <p className="mt-2 text-xs opacity-75">{result.path} · {result.error ? 'sem status' : `HTTP ${result.status}`} · {result.duration} ms</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-7 rounded-2xl bg-[var(--surface-soft)] p-5 text-sm leading-relaxed text-[var(--text-muted)]">
              Se uma tela não abre, rode a verificação e envie o resultado. Isso ajuda a separar erro de código, sessão expirada, indisponibilidade temporária e bloqueio de rede.
            </div>
          )}

          {results.length > 0 && (
            <div className="mt-7 border-t border-[var(--border)] pt-6">
              <p className="text-sm font-bold text-[var(--primary)]">Precisa falar com o suporte?</p>
              <p className="mt-1 text-sm leading-relaxed text-[var(--text-muted)]">Copie o diagnóstico e envie junto da descrição do que aconteceu.</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button type="button" onClick={copyReport} className="rounded-xl border border-[var(--border)] px-4 py-3 text-sm font-bold text-[var(--primary)] hover:bg-[var(--surface-soft)]">
                  {copied ? 'Diagnóstico copiado' : 'Copiar diagnóstico'}
                </button>
                <button type="button" onClick={contactSupport} className="rounded-xl bg-[var(--primary)] px-4 py-3 text-sm font-bold text-[var(--on-accent)] hover:brightness-110">
                  Enviar para o suporte
                </button>
              </div>
              <p className="mt-4 text-xs text-[var(--text-muted)]">Contato: {SUPPORT_EMAIL}</p>
            </div>
          )}
        </section>

        <section className="mt-8 grid gap-4 md:grid-cols-3">
          {[
            ['Se aparecer “Funcionando”', 'O navegador alcançou o MiniMental. O problema pode ser sessão, formulário ou fluxo específico.'],
            ['Se aparecer “Falha da aplicação”', 'A resposta veio como erro da própria aplicação; o caminho e o status ajudam a localizar o ponto.'],
            ['Se aparecer “Possível bloqueio na borda”', 'Há um sinal compatível com proteção de rede. O suporte pode comparar horário e rota com os registros do Cloudflare.'],
          ].map(([title, detail]) => (
            <article key={title} className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-5">
              <h2 className="font-bold text-[var(--primary)]">{title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-[var(--text-muted)]">{detail}</p>
            </article>
          ))}
        </section>
      </section>
    </main>
  );
}
